import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { faLinkedin, faReddit } from "@fortawesome/free-brands-svg-icons";
import { SparklesIcon } from "@heroicons/react/24/solid";
import InputChipComponent from "../../components/InputChipComponent/InputChipComponent";
import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";

const CHANNELS = [
  { id: "reddit", label: "Reddit", description: "No extension needed. We run your scout on a schedule." },
  { id: "linkedin", label: "LinkedIn", description: "Requires the LeadZLab browser extension. Connect LinkedIn to start." },
];

const USE_CASE_LABELS = {
  "freelance-gigs": "Find clients or gigs",
  clients: "Find clients or gigs",
  recommendations: "Get recommended",
  "hire-for-team": "Hire for my team",
  "market-research": "See what people are asking",
  other: "Something else",
};

const CreateJobPage = () => {
  document.title = `Create scout | LeadZLab`;

  const [searchParams] = useSearchParams();
  const urlUseCase = searchParams.get("useCase") || "";
  const urlChannel = searchParams.get("channel") || "";

  const [objective, setObjective] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [subReddits, setSubreddits] = useState([]);
  const [jobName, setJobName] = useState("");
  const [linkedInQuery, setLinkedInQuery] = useState("");
  const [selectedType, setSelectedType] = useState(
    urlChannel === "reddit" ? "reddit" : "linkedin"
  );
  const [isUserConnectedWithLinkedIn, setIsUserConnectedWithLinkedIn] =
    useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [queryPrompt, setQueryPrompt] = useState("");
  const [querySuggestLoading, setQuerySuggestLoading] = useState(false);
  const [redditHiringIntentGate, setRedditHiringIntentGate] = useState(false);
  const [redditHiringIntentDesignerPatterns, setRedditHiringIntentDesignerPatterns] = useState(false);

  // * Auth context
  const { user } = useAuthContext();

  const navigate = useNavigate();
  const fromOnboarding = Boolean(urlUseCase);
  const useCaseLabel = USE_CASE_LABELS[urlUseCase] || urlUseCase || "your use case";

  // * Check if user is logged in with Linkedin
  const checkLinkedInConnection = async () => {
    try {
      await axios.get(`${environment.BACKEND}/user/check-linkedin`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      setIsUserConnectedWithLinkedIn(true);
    } catch (err) {
      setIsUserConnectedWithLinkedIn(false);
    }
  };

  useEffect(() => {
    checkLinkedInConnection();
  }, []);

  useEffect(() => {
    if (urlChannel === "reddit" || urlChannel === "linkedin") {
      setSelectedType(urlChannel);
    }
  }, [urlChannel]);

  const applySuggestion = (d) => {
    if (d?.jobName) setJobName(d.jobName);
    if (d?.objective) setObjective(d.objective);
    else if (Array.isArray(d?.keywords) && d.keywords.length) setObjective(d.keywords.join(", "));
    if (d?.negativePrompt) setNegativePrompt(d.negativePrompt);
    if (Array.isArray(d?.subreddits) && d.subreddits.length) setSubreddits(d.subreddits);
    if (d?.q && selectedType === "linkedin") setLinkedInQuery(d.q);
  };

  const fillFromSuggestion = async () => {
    if (!urlUseCase) return;
    setSuggestionLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/onboarding/auto-generate-matcher`,
        { useCase: urlUseCase, channel: urlChannel || selectedType },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const d = res?.data || res;
      applySuggestion(d);
      toast.success("Suggestion applied. You can edit and save.");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not load suggestion");
    } finally {
      setSuggestionLoading(false);
    }
  };

  const fetchScoutFromProfile = async () => {
    setSuggestionLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/onboarding/generate-scout-from-profile`,
        { channel: selectedType },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const d = res?.data || res;
      applySuggestion(d);
      toast.success("Scout generated from your profile.");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Could not generate from profile");
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleJobNameChange = (e) => {
    setJobName(e.target.value);
  };

  const fillQueryWithAi = async () => {
    const prompt = (queryPrompt || "").trim();
    if (!prompt) return;
    setQuerySuggestLoading(true);
    try {
      const { data: res } = await axios.post(
        `${environment.BACKEND}/user/suggest-linkedin-queries`,
        { prompt },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } }
      );
      const query = res?.data?.query;
      if (query) setLinkedInQuery(query);
      if (query) toast.success("Search queries applied. You can edit before saving.");
    } catch (err) {
      const msg = err?.response?.data?.message ?? err.message;
      toast.error(msg);
    } finally {
      setQuerySuggestLoading(false);
    }
  };

  const handleCreateJob = async () => {
    setCreateLoading(true);
    try {
      if (selectedType === "linkedin") {
        const q = (linkedInQuery || "").trim();
        if (!q) throw new Error("Please enter a search query for LinkedIn");
        if (q.length > 500)
          throw new Error("Search query must be 500 characters or less.");
        await axios.post(
          `${environment.BACKEND}/resources/job`,
          {
            jobName: jobName,
            q,
            objective: (objective || "").trim(),
            negativePrompt: (negativePrompt || "").trim(),
            timeFilter: "day",
            type: selectedType,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      } else if (selectedType === "reddit") {
        if (subReddits.length === 0)
          throw new Error("Please add at least one subreddit. We'll validate that each exists before saving.");

        await axios.post(
          `${environment.BACKEND}/resources/job`,
          {
            jobName: jobName,
            subreddits: subReddits.map((s) => (s.startsWith("r/") ? s.slice(2) : s).trim()).filter(Boolean),
            objective: (objective || "").trim(),
            negativePrompt: (negativePrompt || "").trim(),
            timeFilter: "day",
            type: selectedType,
            redditHiringIntentGate,
            redditHiringIntentDesignerPatterns,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      }

      toast.success(`Scout created successfully`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
      navigate("/jobs");
    } catch (err) {
      toast.error(`${err?.response?.data?.message ?? err.message}`, {
        position: "top-right",
        autoClose: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const suggestedSubreddits = ["forhire", "freelance", "designjobs", "graphic_design", "creativejobs", "startups"];

  const addSuggestedSubreddit = (sub) => {
    const n = (sub.startsWith("r/") ? sub.slice(2) : sub).trim().toLowerCase();
    if (n && !subReddits.includes(n)) setSubreddits([...subReddits, n]);
  };

  return (
    <div className="mx-auto max-w-xl">
      {/* Compact header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Create scout</h1>
        <p className="mt-1 text-sm text-slate-500">We’ll run it on a schedule and notify you when we find matches.</p>
      </div>

      {/* Single form card */}
      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/50 overflow-hidden">
        <div className="p-6 sm:p-8 space-y-6">
          {/* Channel row + AI fill */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2">
              {CHANNELS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSelectedType(opt.id)}
                  className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition ${
                    selectedType === opt.id
                      ? "border-emerald-500 bg-emerald-500 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {opt.id === "reddit" && <FontAwesomeIcon icon={faReddit} className="text-lg" />}
                  {opt.id === "linkedin" && <FontAwesomeIcon icon={faLinkedin} className="text-lg" />}
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchScoutFromProfile}
                disabled={suggestionLoading}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
              >
                <SparklesIcon className="h-4 w-4" />
                {suggestionLoading ? "Generating…" : "Use my profile"}
              </button>
              {fromOnboarding && urlUseCase && (
                <>
                  <span className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={fillFromSuggestion}
                    disabled={suggestionLoading}
                    className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  >
                    Fill for {useCaseLabel}
                  </button>
                </>
              )}
            </div>
          </div>

          {selectedType === "linkedin" && isUserConnectedWithLinkedIn !== null && (
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                isUserConnectedWithLinkedIn
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-800"
              }`}
            >
              {isUserConnectedWithLinkedIn ? (
                <span className="inline-flex items-center gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <FontAwesomeIcon icon={faTimesCircle} />
                  <a
                    href="https://docs.google.com/document/d/14impr-1XtM4_aNnWHOkM5OyvoYa7F4ckeRJvkGE9llQ/edit?usp=sharing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                  >
                    Connect extension
                  </a>
                </span>
              )}
            </div>
          )}

          {/* Scout name */}
          <div>
            <label htmlFor="jobName" className="block text-sm font-medium text-slate-700 mb-1.5">
              Name
            </label>
            <input
              type="text"
              id="jobName"
              placeholder="e.g. Freelance design leads"
              value={jobName}
              onChange={handleJobNameChange}
              className="input-base w-full rounded-lg"
            />
          </div>

          {/* LinkedIn: AI query suggestion + search query */}
          {selectedType === "linkedin" && (
            <div>
              <div className="mb-3">
                <label htmlFor="queryPrompt" className="block text-sm font-medium text-slate-700 mb-1">
                  Describe who you want to find
                </label>
                <input
                  type="text"
                  id="queryPrompt"
                  placeholder='e.g. people looking for alternatives to Gong, switching from Aircall'
                  value={queryPrompt}
                  onChange={(e) => setQueryPrompt(e.target.value)}
                  className="input-base w-full rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={fillQueryWithAi}
                  disabled={querySuggestLoading || !queryPrompt.trim()}
                  className="mt-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {querySuggestLoading ? "Generating…" : "Suggest search queries with AI"}
                </button>
              </div>
              <label htmlFor="linkedinQuery" className="block text-sm font-medium text-slate-700 mb-1.5">
                Search query
              </label>
              <input
                type="text"
                id="linkedinQuery"
                placeholder="e.g. graphic design, logo design — or use AI above"
                value={linkedInQuery}
                onChange={(e) => setLinkedInQuery(e.target.value)}
                className="input-base w-full rounded-lg"
              />
              <div className="mt-4">
                <label htmlFor="objectiveLinkedIn" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Objective
                  <span className="ml-1.5 font-normal text-slate-400">(what kind of leads you want — AI uses this to filter posts)</span>
                </label>
                <textarea
                  id="objectiveLinkedIn"
                  placeholder="e.g. Clients looking for logo and brand design, or hiring a freelance graphic designer"
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  rows={3}
                  className="input-base w-full rounded-lg resize-y"
                />
              </div>
              <div className="mt-4">
                <label htmlFor="negativePromptLinkedIn" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Exclude / negative prompt
                  <span className="ml-1.5 font-normal text-slate-400">(what to filter out)</span>
                </label>
                <textarea
                  id="negativePromptLinkedIn"
                  placeholder="e.g. Remote-only, unpaid, internship only"
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  rows={2}
                  className="input-base w-full rounded-lg resize-y"
                />
              </div>
            </div>
          )}

          {/* Reddit: objective + negative prompt + subreddits (no keywords) */}
          {selectedType === "reddit" && (
            <>
              <div>
                <label htmlFor="objective" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Objective
                  <span className="ml-1.5 font-normal text-slate-400">(what kind of leads you want — AI uses this to score posts)</span>
                </label>
                <textarea
                  id="objective"
                  placeholder="e.g. Clients looking for logo and brand design, or hiring a freelance graphic designer"
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  rows={3}
                  className="input-base w-full rounded-lg resize-y"
                />
              </div>
              <div>
                <label htmlFor="negativePrompt" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Exclude / negative prompt
                  <span className="ml-1.5 font-normal text-slate-400">(what to filter out — e.g. remote only, unpaid, [For Hire] posts)</span>
                </label>
                <textarea
                  id="negativePrompt"
                  placeholder="e.g. Remote-only roles, unpaid or equity-only, posts that say [For Hire]"
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  rows={2}
                  className="input-base w-full rounded-lg resize-y"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Subreddits</label>
                <InputChipComponent
                  keywords={subReddits}
                  setKeywords={setSubreddits}
                  placeholder="e.g. forhire, freelance, designjobs"
                />
                <p className="mt-1.5 text-xs text-slate-500">We’ll check that each subreddit exists before saving.</p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {suggestedSubreddits.map((sub) => (
                    <button
                      key={sub}
                      type="button"
                      onClick={() => addSuggestedSubreddit(sub)}
                      disabled={subReddits.includes(sub)}
                      className={`rounded-md px-2 py-1 text-xs font-medium transition ${
                        subReddits.includes(sub) ? "bg-emerald-100 text-emerald-800" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                      }`}
                    >
                      r/{sub}
                    </button>
                  ))}
                  <a
                    href="https://www.youtube.com/watch?v=y5PsnWsWUBI"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 text-xs text-slate-400 hover:text-slate-600"
                  >
                    Demo →
                  </a>
                </div>
              </div>
              <details className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm">
                <summary className="cursor-pointer font-medium text-slate-700">Advanced (optional)</summary>
                <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      checked={redditHiringIntentGate}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setRedditHiringIntentGate(on);
                        if (!on) setRedditHiringIntentDesignerPatterns(false);
                      }}
                    />
                    <span>
                      <span className="font-medium text-slate-800">Require hiring-intent text gate</span>
                      <span className="mt-0.5 block text-xs text-slate-600">
                        After the LLM approves a post, require hiring/paid keywords before saving as a lead. Off by default.
                      </span>
                    </span>
                  </label>
                  {redditHiringIntentGate && (
                    <label className="flex cursor-pointer items-start gap-2 pl-6">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                        checked={redditHiringIntentDesignerPatterns}
                        onChange={(e) => setRedditHiringIntentDesignerPatterns(e.target.checked)}
                      />
                      <span>
                        <span className="font-medium text-slate-800">Graphic designer keyword patterns (legacy)</span>
                        <span className="mt-0.5 block text-xs text-slate-600">
                          Extra patterns for designer/logo/brand leads. Only if that matches your niche.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              </details>
            </>
          )}

        </div>

        {/* CTA bar */}
        <div className="px-6 sm:px-8 py-4 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-3">
          {createLoading && (
            <span className="inline-flex items-center gap-2 text-sm text-slate-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
              Creating scout…
            </span>
          )}
          <button
            type="button"
            className="rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[8rem]"
            onClick={handleCreateJob}
            disabled={createLoading || (selectedType === "linkedin" && !isUserConnectedWithLinkedIn)}
          >
            {createLoading ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Creating…
              </>
            ) : (
              "Create scout"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateJobPage;
